import { Link } from 'react-router-dom';
import { AlertTriangle, PieChart } from 'lucide-react';
import { Card, EmptyState } from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatMoney } from '../../lib/format';
import type { CashflowReport } from '../cashflow/api';

/**
 * The two blocks that ride beside the charts: Invoices and Breakdown.
 *
 * Every figure is a field of the same `/api/cashflow` response the Budget &
 * Forecast Report tab renders — `report.invoices`, `report.breakdown` — never a
 * second derivation.
 *
 * **Two were deleted here, on Jesse's instruction** (2026-08-12):
 *  - **Runway** was a block *and* a KPI card, disagreeing in the last digit
 *    because one rounded ("2.0 mo" vs "2.02 months"). The KPI stayed.
 *  - **Recurring** listed 12 templates: *"a list of 12 recurring templates is
 *    management detail, not a headline"*. It lives on /invoices/recurring.
 */

export function InvoicesBlock({ report }: { report: CashflowReport }) {
  // An OPEN row prints what is still OWED (`due`), not the invoice's face value:
  // a partly-paid overdue invoice is $12,225 of cash to collect, not $19,225 of
  // paper, and /invoices' Overdue tile says the same. Paid prints face value —
  // `due` on a paid invoice is zero by definition.
  //
  // ⚠ **The Draft row is the one row that is NOT receivable money**, and it says
  // so on itself. A draft has never been sent, so nobody owes it, it cannot be
  // overdue and it is not in the forecast's Income row — which is why these four
  // rows do not add up to anything on the grid, and why the row needs the three
  // words rather than a footnote. (Before this, drafts were silently inside
  // expected cash AND a past-due draft was rolled forward as overdue, so the
  // bonus card two rows up read "8 overdue · $149,416" beside this block's
  // correct "Overdue 7 · $120,985" — one API response, two answers.)
  const rows: Array<{
    key: string;
    label: string;
    value: number;
    count: number;
    to: string;
    tone: string;
    note?: string;
  }> = [
    { key: 'draft', label: 'Draft', value: report.invoices.draft.due, count: report.invoices.draft.count, to: '/invoices?state=DRAFT', tone: 'text-status-draft', note: 'not sent · scheduled ones are in Income' },
    { key: 'sent', label: 'Sent', value: report.invoices.sent.due, count: report.invoices.sent.count, to: '/invoices?state=OPEN', tone: 'text-status-open' },
    { key: 'overdue', label: 'Overdue', value: report.invoices.overdue.due, count: report.invoices.overdue.count, to: '/invoices?state=LATE', tone: 'text-status-late' },
    // ⚠⚠ **THIS ROW IS PAPER, AND IT IS NOT REVENUE.** It used to be labelled
    // "collected · revenue" and this comment used to call it "the one row that
    // is revenue — the same substance as the Revenue vs goal tile". Both were
    // false. It is the FACE VALUE of the invoices this year ISSUED that have
    // since settled ($900,985 on seed), not cash: it drops every {year} payment
    // against a {year-1} invoice and every part payment on a still-open one —
    // $103,853.75 of real money — while cash collected in 2026 is $1,004,839,
    // the figure the Breakdown card beside this one splits. The block's footing
    // states the difference; the row states its own window.
    { key: 'paid', label: 'Paid', value: report.invoices.paid.total, count: report.invoices.paid.count, to: '/invoices?state=PAID', tone: 'text-status-paid', note: `issued in ${report.year} · settled` },
  ];
  return (
    <Card padding="none" className="overflow-hidden">
      {/* Draft/Sent/Overdue are AR right now — the same set /invoices counts, so
          the two screens can never print different digits under "Overdue".
          Paid is what this year ISSUED and has since settled. Every row here is
          a fact about invoices; none of them is a cash figure. */}
      <BlockHeader title="Invoices" note={`Open now · issued in ${report.year}`} to="/invoices" />
      {/* `flex-1` + a flexed row each: when this card is stretched to match a
          taller sibling (the app-wide fill rule), the rows share the extra
          height instead of stranding it under the last one. */}
      <ul className="flex flex-1 flex-col divide-y divide-grey-100">
        {rows.map((row) => (
          <li key={row.key} className="flex-1">
            <Link
              to={row.to}
              className="flex h-full items-center justify-between gap-3 px-4 py-2 transition-colors hover:bg-grey-50"
            >
              <span className="flex min-w-0 items-baseline gap-2">
                <span className={cn('text-body-sm font-semibold', row.tone)}>{row.label}</span>
                <span className="num text-caption text-grey-400">{row.count}</span>
                {row.note && row.count > 0 && (
                  <span className="truncate text-caption text-grey-400">{row.note}</span>
                )}
              </span>
              <span className="num text-body-sm font-semibold text-secondary">
                {formatMoney(row.value, { currency: report.currency, whole: true })}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {/* ⚠⚠ **THE RECONCILIATION, IN WORDS, ONCE.** Three figures on this one
          screen used to be called "collected" and they were $76,306 and
          $103,854 apart: this block's Paid row ($900,985, and not cash at all),
          the Revenue vs goal tile ($977,291, cash through the last CLOSED
          period) and the Breakdown card ($1,004,839, cash so far this year).
          Each now names its own window on itself; this sentence names the one
          that is not cash at all, and points at the two that are. It is the
          ageing/forecast discipline — name yourself, then name the other. */}
      <p className="border-t border-grey-100 px-4 py-2 text-caption leading-relaxed text-grey-500">
        Invoice paper, not revenue. Revenue is the cash that arrived —{' '}
        <Link to="/reports/pnl" className="font-semibold text-grey-600 underline-offset-2 hover:underline">
          {formatMoney(report.totals.collected, { currency: report.currency, whole: true })} collected
          in {report.year}
        </Link>
        , which also counts payments on invoices issued earlier. The Breakdown card splits it.
      </p>
      {report.invoices.overdue.due > 0 && (
        // ⚠ HALF OF THE TWO-SIDED OVERDUE RULE, and the Receivables ageing card
        // three inches away is the other half. This screen now carries BOTH
        // readings of the same invoices, so each says which it is in words:
        // here the money is rolled FORWARD (when will it arrive), there it is
        // aged BACKWARDS by due date (how late is it).
        <p className="border-t border-grey-100 bg-status-late-bg/60 px-4 py-2 text-caption leading-relaxed text-status-late">
          <AlertTriangle className="mr-1 inline h-3.5 w-3.5 align-text-bottom" aria-hidden />
          {formatMoney(report.invoices.overdue.due, { currency: report.currency, whole: true })} past
          due is rolled <strong className="font-semibold">forward</strong> into the current period —
          when we expect to collect it. The Receivables ageing card ages the same invoices{' '}
          <strong className="font-semibold">backwards</strong> by due date.
        </p>
      )}
    </Card>
  );
}

export function BreakdownBlock({
  report,
  className,
}: {
  report: CashflowReport;
  /** The dashboard's three-column row hands this the full width of the
   *  two-column tablet step — it is the odd card out there, and four
   *  label/value rows read fine at any width, whereas a half-empty grid line
   *  beside them is just a hole. */
  className?: string;
}) {
  const { breakdown } = report;
  const money = (value: number) => formatMoney(value, { currency: report.currency, whole: true });
  // ⚠ **THE FIRST ROW IS THE APP'S REVENUE FIGURE, and it was missing from
  // its own dashboard.** This screen printed $977,291 (cash through the last
  // closed period) and $900,985 (invoice paper) and never once printed
  // $1,004,839 — the number `/reports/pnl`, `/api/dashboard` and this report's
  // own `totals.collected` all agree on. The three family rows below it are
  // that figure split, so the card now foots to something a reader can check.
  const rows = [
    { label: `Collected in ${report.year}`, amount: report.totals.collected, strong: true },
    { label: 'Support + recurring', amount: breakdown.supportRecurringTotal, strong: false },
    { label: 'Avg support / month', amount: breakdown.avgSupportMonthly },
    { label: 'Avg recurring / month', amount: breakdown.avgRecurringMonthly },
    { label: 'Avg agreement / month', amount: breakdown.avgAgreementMonthly },
  ];
  /** Nothing collected in any family — a card of four $0 rows is not a
   *  breakdown, it is a shape pretending to be data. */
  const empty = rows.every((row) => row.amount === 0);
  return (
    <Card padding="none" className={cn('overflow-hidden', className)}>
      {/* The window, said on the card: this is ALL of the year's cash so far,
          where the Revenue vs goal tile deliberately stops at the last closed
          period so both sides of its comparison cover the same fortnight. */}
      <BlockHeader
        title="Breakdown"
        note={
          breakdown.monthsElapsed > 0
            ? `Cash received · ${breakdown.monthsElapsed} month${breakdown.monthsElapsed === 1 ? '' : 's'} of ${report.year}`
            : 'Nothing collected yet'
        }
      />
      {empty ? (
        <EmptyState
          className="px-4 py-8"
          icon={<PieChart aria-hidden />}
          title="Nothing collected yet"
          description={`No support, recurring or agreement revenue has been collected in ${report.year}.`}
        />
      ) : (
        <ul className="flex flex-1 flex-col divide-y divide-grey-100">
          {rows.map((row) => (
            <li key={row.label} className="flex flex-1 items-center justify-between gap-3 px-4 py-2">
              <span
                className={cn(
                  'text-body-sm',
                  row.amount === 0
                    ? 'text-grey-400'
                    : row.strong
                      ? 'font-semibold text-secondary'
                      : 'text-grey-600',
                )}
              >
                {row.label}
              </span>
              {/* A zero here means "this family earned nothing", and it is
                  muted to the same grey the ageing tiles use for an empty
                  bucket — so a reader's eye lands on the one row that has
                  money rather than sweeping four equally-black figures. */}
              <span
                className={cn(
                  'num text-body-sm',
                  row.amount === 0
                    ? 'text-grey-300'
                    : row.strong
                      ? 'font-bold text-secondary'
                      : 'font-semibold text-grey-700',
                )}
              >
                {row.amount === 0 ? '—' : money(row.amount)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {!empty && (
        // ⚠ **THE SPLIT RULE, NAMED.** These families are allocated across
        // each payment's LINE items, not by the invoice's single `kind` — so a
        // support line on a mixed agreement invoice is support revenue. This card
        // and /reports/pnl used to disagree by $29,033.75 on exactly that,
        // under totals that matched to the cent. One allocation now
        // (`pnl/revenue.ts`), and the rule is on screen rather than in it.
        <p className="border-t border-grey-100 px-4 py-2 text-caption leading-relaxed text-grey-500">
          Split by what each payment paid for — a support line on a mixed invoice is support
          revenue, the same way{' '}
          <Link
            to="/reports/pnl"
            className="font-semibold text-grey-600 underline-offset-2 hover:underline"
          >
            Profit &amp; Loss
          </Link>{' '}
          splits it.
        </p>
      )}
    </Card>
  );
}

function BlockHeader({ title, note, to }: { title: string; note?: string; to?: string }) {
  // ⚠ ONE TITLE TREATMENT ACROSS THE DASHBOARD (Jesse, 2026-08-20: "make sure
  // the card titles are consistent"). This header printed caption-sized grey
  // CAPS while every card around it used heading-4 secondary — the same
  // screen carrying two ranks of title, which reads as two ranks of card.
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-grey-100 px-4 py-2.5">
      <h3 className="text-heading-4 text-secondary">
        {to ? (
          <Link
            to={to}
            className="rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
          >
            {title}
          </Link>
        ) : (
          title
        )}
      </h3>
      {note && <span className="truncate text-caption text-grey-400">{note}</span>}
    </div>
  );
}
